import {Component, computed, inject, input} from "@angular/core"
import {toSignal} from "@angular/core/rxjs-interop"
import {MatMenuModule} from "@angular/material/menu"
import {MatTooltipModule} from "@angular/material/tooltip"
import {ButtonComponent} from "@common/components/buttons/button/button.component"
import {InputContainerComponent} from "@common/components/inputs/input-container/input-container.component"
import {ValueSlotComponent} from "@app/template-editor/components/value-slot/value-slot.component"
import {SceneManagerService} from "@app/template-editor/services/scene-manager.service"
import {
    MetaData,
    isValidExternalId,
    isNumberLike,
    isBooleanLike,
    isStringLike,
    isJsonLike,
    stringLike,
    numberLike,
    booleanLike,
    jsonLike,
} from "@cm/template-nodes"
import type {TemplateNode} from "@cm/template-nodes"

type MetaDataNodeParameters = {metaData?: MetaData}

@Component({
    selector: "cm-meta-data-list",
    templateUrl: "./meta-data-list.component.html",
    styleUrl: "./meta-data-list.component.scss",
    imports: [ButtonComponent, InputContainerComponent, ValueSlotComponent, MatTooltipModule, MatMenuModule],
})
export class MetaDataListComponent {
    private readonly sceneManagerService = inject(SceneManagerService)
    private readonly $templateTreeChanges = toSignal(this.sceneManagerService.templateTreeChanged$)

    readonly $node = input.required<TemplateNode<MetaDataNodeParameters>>({alias: "node"})

    readonly $metaData = computed<MetaData | undefined>(() => {
        this.$templateTreeChanges()
        return this.$node().parameters.metaData
    })

    readonly $entries = computed(() => {
        this.$templateTreeChanges()
        const metaData = this.$node().parameters.metaData
        if (!metaData) return []
        const {metaData: _nested, ...params} = metaData.parameters
        return Object.entries(params)
            .filter(([, value]) => value !== undefined)
            .map(([key, value]) => ({
                key,
                schema: isNumberLike(value)
                    ? numberLike
                    : isBooleanLike(value)
                      ? booleanLike
                      : isStringLike(value)
                        ? stringLike
                        : isJsonLike(value)
                          ? jsonLike
                          : stringLike,
            }))
    })

    addMetaData(type: "string" | "number" | "boolean" | "json") {
        this.sceneManagerService.modifyTemplateGraph(() => {
            const node = this.$node()
            let metaData = this.$node().parameters.metaData
            if (!metaData) {
                metaData = new MetaData({parameters: {}})
                node.updateParameters({metaData})
            }
            let key = ""
            let counter = 1
            while (key === "" || key in metaData.parameters) {
                key = `metaData${counter}`
                counter++
            }
            const initialValue = type === "number" ? 0 : type === "boolean" ? false : type === "json" ? {} : ""
            metaData.updateParameters({[key]: initialValue})
        })
    }

    removeMetaData(key: string) {
        this.sceneManagerService.modifyTemplateGraph(() => {
            const metaData = this.$node().parameters.metaData
            if (!metaData) return
            metaData.updateParameters({[key]: undefined})
            const {metaData: _nested, ...remaining} = metaData.parameters
            const hasEntries = Object.values(remaining).some((v) => v !== undefined)
            if (!hasEntries) {
                this.$node().updateParameters({metaData: undefined})
            }
        })
    }

    renameKey(oldKey: string, inputElement: HTMLInputElement) {
        const newKey = inputElement.value
        if (oldKey === newKey) return
        const metaData = this.$node().parameters.metaData
        if (!metaData || !isValidExternalId(newKey) || (newKey in metaData.parameters && newKey !== oldKey)) {
            inputElement.value = oldKey
            return
        }
        this.sceneManagerService.modifyTemplateGraph(() => {
            const newParams = Object.fromEntries(Object.entries(metaData.parameters).map(([k, v]) => [k === oldKey ? newKey : k, v]))
            metaData.replaceParameters(newParams)
        })
    }
}
